import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, AuthContextType } from '../types/auth';
import authService from '../services/authService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 调试：监听user状态变化
  useEffect(() => {
    console.log('🔄 AuthContext: user状态变化 ->', user ? `${user.email} (已登录)` : 'null (未登录)');
  }, [user]);

  // 调试：监听loading状态变化
  useEffect(() => {
    console.log('🔄 AuthContext: loading状态变化 ->', loading);
  }, [loading]);

  // 初始化时检查用户登录状态
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🔍 AuthContext 初始化: 检查登录状态...');
      
      if (authService.isAuthenticated()) {
        try {
          console.log('✅ 发现访问令牌，获取用户信息...');
          const currentUser = await authService.getCurrentUser();
          console.log('✅ 获取用户信息成功:', currentUser.email);
          setUser(currentUser);
        } catch (error) {
          console.error('❌ 获取用户信息失败:', error);
          // 清除无效的令牌
          authService.clearTokens();
          setError('登录状态已过期，请重新登录');
        }
      } else {
        console.log('ℹ️ 未发现访问令牌，用户未登录');
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔐 开始邮箱登录:', email);
      
      const result = await authService.login({ email, password });
      console.log('✅ 登录成功:', result.user.email);
      
      setUser(result.user);
    } catch (err: any) {
      console.error('❌ 登录失败:', err);
      const errorMessage = err.message || '登录失败，请重试';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log('📝 开始用户注册:', email);
      
      const result = await authService.register({ email, password, displayName });
      console.log('✅ 注册成功:', result.user.email);
      
      setUser(result.user);
    } catch (err: any) {
      console.error('❌ 注册失败:', err);
      const errorMessage = err.message || '注册失败，请重试';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      console.log('🚪 开始用户登出...');
      
      await authService.logout();
      console.log('✅ 用户已登出，状态已清空');
      
      setUser(null);
    } catch (err: any) {
      console.error('❌ 登出失败:', err);
      setError('登出失败，请重试');
      // 即使登出请求失败，也清空本地状态
      setUser(null);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setError(null);
      console.log('🔑 发送密码重置邮件:', email);
      
      await authService.resetPassword(email);
      console.log('✅ 密码重置邮件已发送');
    } catch (err: any) {
      console.error('❌ 发送密码重置邮件失败:', err);
      const errorMessage = err.message || '发送密码重置邮件失败';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const updateUserProfile = async (displayName: string, photoURL?: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log('📝 更新用户资料:', displayName);
      
      const updatedUser = await authService.updateUser({ displayName, photoURL });
      console.log('✅ 用户资料更新成功');
      
      setUser(updatedUser);
    } catch (err: any) {
      console.error('❌ 更新用户资料失败:', err);
      const errorMessage = err.message || '更新用户资料失败';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateUserProfile,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};